import { getBudget, useGetWorkPlan } from "@api/workplanService";
import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, LinearProgress, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { intervalToDuration } from "date-fns";
import { formatCurrency } from "@utils/numbers";
import { FauxTable, FauxTableRow } from "@components/FauxTable";
import { useAuth } from "@providers/AuthProvider";

/*
interface ShowData {
    label?: string;
    conditional?: Function|boolean;
    content?: Function|string;
};
*/

const CardHeader = () => {
    const data = useContext(ObjectContext);
    const avatarInitials = data.name ? data.name.split(' ').map(i => i.charAt(0)).join('').toUpperCase() : '';

    const progress = data.number_tasks ? (((data.number_tasks - data.number_incomplete_tasks) / data.number_tasks) * 100) : 0;

    return (
        <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            p={1.5}
            pt={0}
        >
            <Box sx={{flex: 1}}>
                <Typography fontSize={'md'} fontWeight={'bold'}>{data.name}</Typography>

                {isValidDate(data.workplan_date) && (<Typography fontSize={'sm'}>{formatDate(data.workplan_date)}</Typography>)}

                {parseInt(data.number_tasks) > 0 && (
                <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={2}
                >
                    <Box sx={{flex: 'auto', whiteSpace: 'nowrap'}}>
                        <Typography fontSize={'sm'}>{data.number_tasks - data.number_incomplete_tasks} of {data.number_tasks} tasks</Typography>
                    </Box>
                    <Box sx={{flex: '1 1 100%', display: 'flex', justifyContent: 'flex-end'}}>
                        <LinearProgress determinate value={progress} sx={{backgroundColor: 'var(--joy-palette-background-backdrop)'}} />
                    </Box>
                </Stack>
                )}
            </Box>
        </Stack>
    )
};

const budgetColumn = (label, type) => {
    return {
        label: label,
        content: (object) => {
            return (<>
                <FauxTable>
                    <FauxTableRow title={'Est.'}>
                        <Typography fontSize={'sm'}>{formatCurrency(getBudget(object?.tasks, `estimated_${type}`))}</Typography>
                    </FauxTableRow>
                    <FauxTableRow title={'Actual.'}>
                        <Typography fontSize={'sm'}>{formatCurrency(getBudget(object?.tasks, `actual_${type}`))}</Typography>
                    </FauxTableRow>
                </FauxTable>
            </>)
        },
    };
}

const dataToDisplay/*: readonly ShowData[]*/ = [
    {
        label: 'Event Chair',
        conditional: object => !!object.event_chair,
        content: (object) => <ObjectPreview to={`/contacts/${object.event_chair_id}`} object={object.event_chair}>{object.event_chair.name}</ObjectPreview>,
    },
    budgetColumn('Income', 'income'),
    budgetColumn('Expense', 'expense'),
    {
        label: 'Notes',
        conditional: object => !!object.notes,
        content: (object) => object.notes,
    },
    {
        label: <>Volunteer<br/>Opportunities</>,
        conditional: object => !!object.opportunities.length,
        content: (object) => <>
            {object.opportunities.map((opportunity, opportunityIndex) => (<React.Fragment key={opportunityIndex}>
                <ObjectPreview to={`/opportunities/${opportunity.id}`} object={opportunity}>{opportunity.name}</ObjectPreview>
                <br/>
            </React.Fragment>))}
        </>
    },
];

export default function WorkPlanShow() {
    const {user, can} = useAuth();

    const SendTo = () => {
        return (
            <Tooltip title="Send To...">
                <IconButton
                    size="sm"
                    color="neutral"
                    variant="outlined"
                    sx={{ bgcolor: 'background.surface' }}
                >
                    <FileUploadRoundedIcon/>
                </IconButton>
            </Tooltip>
        );
    }

    const canEdit = ({data}) => {
        if(can('edit work plans')) {
            return true;
        }

        if(can('edit own work plans')) {
            console.log(user);
            const userIsConnected = data.contacts.find(contact => contact.id === user?.contact?.id);
            return !!userIsConnected;
            // return true;
        }

        return false;
    };

    return (
        <ObjectShow
            query={useGetWorkPlan}
            type={'workplan'}
            permissionType={'work plans'}
            title={'Work Plans'}
            subtitleKey={'name'}
            // headerActions={<SendTo/>}
            color={'workplan'}
            canEdit={canEdit}
        >
            <ObjectColumn primary>
                <ObjectPrimaryInfo
                    title={'Work Plan Info'}
                    tint={'workplan'}
                    fields={dataToDisplay}
                    cardHeader={<CardHeader/>}
                />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationship type={'contacts'} />
                <ObjectRelationship type={'involvements'} />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationship type={'notes'} column={'description'} title={'Description'} />
                <ObjectRelationship type={'transformation_strategies'} />
                <ObjectRelationship type={'events'} />
                <ObjectRelationship type={'files'} />
            </ObjectColumn>

            <ObjectColumn full>
                <ObjectRelationship type={'tasks'} />
            </ObjectColumn>

        </ObjectShow>
    )
}
